import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { GanttChart } from '@/components/Scheduling/GanttChart';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  GanttChartSquare,
  Printer,
  ClipboardList,
  PenTool,
  Layers,
  Filter,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';

const ORDER_TYPES = [
  { key: 'all', label: 'Todos', icon: ClipboardList, color: 'text-gray-500' },
  { key: 'plotter', label: 'Plotter', icon: Printer, color: 'text-cyan-500' },
  { key: 'normal', label: 'Normal', icon: ClipboardList, color: 'text-blue-500' },
  { key: 'design_cnc', label: 'Diseño/CNC', icon: PenTool, color: 'text-purple-500' },
  { key: 'mold', label: 'Molde', icon: Layers, color: 'text-orange-500' },
];

export default function ProductionSchedule() {
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [daysToShow, setDaysToShow] = useState(21);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Module Tabs */}
        <ModuleTabs
          tabs={[
            { value: 'calendario', label: 'Calendario', href: '/schedule' },
            { value: 'planificador', label: 'Planificador', href: '/production-planner' },
            { value: 'gantt', label: 'Gantt', href: '/gantt-planner' },
            { value: 'kanban', label: 'Kanban', href: '/production-kanban' },
            { value: 'operarios', label: 'Operarios', href: '/operators-management' },
          ]}
        />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Planificación de Producción
            </h1>
            <p className="text-muted-foreground mt-2">
              Vista Gantt de órdenes de trabajo por tipo
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Type Filter */}
            <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tipo de OT" />
              </SelectTrigger>
              <SelectContent>
                {ORDER_TYPES.map((type) => {
                  const TypeIcon = type.icon;
                  return (
                    <SelectItem key={type.key} value={type.key}>
                      <div className="flex items-center gap-2">
                        <TypeIcon className={`w-4 h-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Days to show */}
            <Select value={String(daysToShow)} onValueChange={(v) => setDaysToShow(Number(v))}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="14">14 días</SelectItem>
                <SelectItem value="21">21 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Type tabs as quick filters */}
        <div className="flex flex-wrap gap-2">
          {ORDER_TYPES.map((type) => {
            const TypeIcon = type.icon;
            const isActive = orderTypeFilter === type.key;
            return (
              <Button
                key={type.key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOrderTypeFilter(type.key)}
                className={isActive ? '' : 'hover:bg-muted'}
              >
                <TypeIcon className={`w-4 h-4 mr-2 ${isActive ? '' : type.color}`} />
                {type.label}
              </Button>
            );
          })}
        </div>

        {/* Legend */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-medium text-muted-foreground">Leyenda:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded bg-cyan-500"></div>
              <span>Plotter</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded bg-blue-500"></div>
              <span>Normal</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded bg-purple-500"></div>
              <span>Diseño/CNC</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded bg-orange-500"></div>
              <span>Molde</span>
            </div>
            <div className="border-l pl-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-green-500"></div>
                <span>Completada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-3 rounded bg-red-500"></div>
                <span>Retrasada</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Gantt Chart */}
        <GanttChart daysToShow={daysToShow} orderTypeFilter={orderTypeFilter} />
      </div>
    </DashboardLayout>
  );
}
